import type { APIState, Result } from '@api';
import { Success, Failure, useErrorMessage, useDatahubAPI } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import type { WeatherLocation } from '@data/datahub/weather/types';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useWeatherLocationsAPI = (): {
  state: APIState;
  getWeatherLocations: () => Promise<WeatherLocation[]>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });
  const getHasScope = useHasScope();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
    ): Promise<Result<WeatherLocation[], AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/weather_locations`,
        );
        return new Success(res?.data?.locations);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getWeatherLocations = useAtomCallback(
    useCallback(
      async (get) => {
        if (!getHasScope(SCOPES.DescribeWeatherData)) {
          setState('hasError');
          return [];
        }
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_weather_locations', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return [];
        }
        setState('loading');
        const res = await request(project.id, environment.environment_id);
        if (!res.value || res.isFailure()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).value?.status !== 404) {
            notifyError({
              message: `${t('api.datahub.get_weather_locations', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          setState('hasError');
          return [];
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, t, request, getErrorMessage, getHasScope],
    ),
  );

  return {
    state,
    getWeatherLocations,
  };
};
